<template>
  <main class="item-page">
    <div class="item-shell">
      <section class="left-pane">
        <h1 class="item-title">{{ title }}</h1>

        <div class="image-wrap" :class="{ 'image-wrap--empty': !hasImage }">
          <div
            v-if="hasImage"
            class="image-frame"
            @click="openModal(currentIndex)"
            role="button"
            tabindex="0"
            @keydown.enter="openModal(currentIndex)"
          >
            <img :src="currentImageSrc" :alt="title" class="image" @error="onImageError(currentIndex)" ref="mainImg" />
            <div v-if="images.length > 1" class="img-counter">{{ currentIndex + 1 }} / {{ images.length }}</div>
          </div>

          <div v-else class="no-image">
            <div class="no-image-text-inline">Изображение не найдено</div>
          </div>
        </div>

        <div v-if="images.length > 1" class="pager" aria-hidden="false">
          <button
            v-for="(img, i) in images"
            :key="img + '_' + i"
            class="pager-dot"
            :class="{ 'pager-dot--active': currentIndex === i }"
            @click="currentIndex = i"
            :aria-label="`Показать изображение ${i + 1}`"
          ></button>
        </div>

        <div class="actions">
          <button class="action-btn" @click="openRequestInfoModal" type="button">Запросить данные по образцу</button>

          <a
            v-if="isFullSample && downloadUrl"
            :href="downloadUrl"
            class="action-btn download-btn"
            target="_blank"
            rel="noopener"
            >Скачать полный набор данных</a
          >
        </div>
      </section>

      <section class="right-pane">
        <div class="panel">
          <div class="subsection">
            <h3 class="subsection-title">Описание образца</h3>
            <dl class="meta">
              <div class="row">
                <dt>Форма</dt>
                <dd>{{ displayValue({ key: 'form' }) }}</dd>
              </div>
              <div class="row">
                <dt>Размер, мм</dt>
                <dd>{{ formatNumberField('size_mm') }}</dd>
              </div>
              <div class="row">
                <dt>Масштаб</dt>
                <dd>{{ displayValue({ key: 'research_scale' }) }}</dd>
              </div>
              <div class="row">
                <dt>Краткое описание</dt>
                <dd>{{ displayValue({ key: 'short_description' }) }}</dd>
              </div>
            </dl>
          </div>

          <div v-if="showGeoLabSections" class="subsection">
            <h3 class="subsection-title">Геологическая информация</h3>
            <dl class="meta">
              <div class="row">
                <dt>Категория</dt>
                <dd>{{ displayValue({ key: 'gis_category' }) }}</dd>
              </div>
              <div class="row">
                <dt>Бассейн</dt>
                <dd>{{ displayValue({ key: 'basin' }) }}</dd>
              </div>
              <div class="row">
                <dt>Структура I/II порядка</dt>
                <dd>{{ combinedStructure }}</dd>
              </div>
              <div class="row">
                <dt>Возраст</dt>
                <dd>{{ displayValue({ key: 'department' }) }}</dd>
              </div>
              <div class="row">
                <dt>Ярус</dt>
                <dd>{{ displayValue({ key: 'tier' }) }}</dd>
              </div>
              <div class="row">
                <dt>Свита / горизонт</dt>
                <dd>{{ displayValue({ key: 'suite_horizon' }) }}</dd>
              </div>
              <div class="row">
                <dt>Пласт</dt>
                <dd>{{ displayValue({ key: 'layer' }) }}</dd>
              </div>
              <div class="row">
                <dt>Генезис</dt>
                <dd>{{ displayValue({ key: 'sample_origin_class' }) }}</dd>
              </div>
              <div class="row">
                <dt>Текстуры по КТ</dt>
                <dd>{{ displayValue({ key: 'textures_ct' }) }}</dd>
              </div>
              <div class="row">
                <dt>Обстановка осадконакопления</dt>
                <dd>{{ displayValue({ key: 'depositional_environment' }) }}</dd>
              </div>
              <div class="row">
                <dt>Фация</dt>
                <dd>{{ displayValue({ key: 'facies' }) }}</dd>
              </div>
            </dl>
          </div>

          <div class="subsection">
            <h3 class="subsection-title">КТ-система</h3>
            <dl class="meta">
              <div class="row">
                <dt>Томограф</dt>
                <dd>{{ displayValue({ key: 'scanner' }) }}</dd>
              </div>
              <div class="row">
                <dt>Фильтр</dt>
                <dd>{{ displayValue({ key: 'filter_used' }) }}</dd>
              </div>
              <div class="row">
                <dt>Напряжение, кВ</dt>
                <dd>{{ formatNumberField('voltage', 0) }}</dd>
              </div>
              <div class="row">
                <dt>Ток, мА</dt>
                <dd>{{ formatNumberField('current_ma', 0) }}</dd>
              </div>
              <div class="row">
                <dt>Разрешение, мкм</dt>
                <dd>{{ formatNumberField('resolution', 2) }}</dd>
              </div>
            </dl>
          </div>

          <div v-if="showGeoLabSections" class="subsection">
            <h3 class="subsection-title">Лабораторные данные</h3>
            <dl class="meta">
              <div class="row">
                <dt>Пористость, %</dt>
                <dd>{{ formatNumberField('porosity', 2) }}</dd>
              </div>
              <div class="row">
                <dt>Проницаемость, мД</dt>
                <dd>{{ formatNumberField('permeability', 1) }}</dd>
              </div>
              <div class="row">
                <dt>Дополнительные лабораторные данные</dt>
                <dd>{{ displayValue({ key: 'other_lab_data' }) }}</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>
    </div>

    <teleport to="body">
      <transition name="fade">
        <div v-if="isModalOpen" class="iv-modal" @click.self="closeModal" role="dialog" aria-modal="true">
          <button class="iv-modal-close" @click="closeModal" aria-label="Close image">✕</button>

          <template v-if="!hasImage || modalImageError">
            <div class="modal-noimage">
              <div class="noimage-box">
                <div class="noimage-ico">🖼️</div>
                <div class="noimage-text">Изображение отсутствует</div>
                <div class="noimage-sub">Нет доступных превью для этого образца.</div>
              </div>
            </div>
          </template>

          <template v-else>
            <button v-if="images.length > 1" class="iv-nav left" @click.stop="prevImage" aria-label="Previous image">
              ‹
            </button>

            <div class="iv-modal-inner" @click.stop>
              <img :src="currentModalImage" class="iv-modal-img" alt="Full size" @error="onModalImageError" />
              <div v-if="images.length > 1" class="iv-modal-indicator">{{ modalIndex + 1 }} / {{ images.length }}</div>
            </div>

            <button v-if="images.length > 1" class="iv-nav right" @click.stop="nextImage" aria-label="Next image">
              ›
            </button>
          </template>
        </div>
      </transition>
    </teleport>

    <teleport to="body">
      <transition name="fade">
        <div
          v-if="showRequestInfoModal"
          class="iv-modal"
          @click.self="closeRequestInfoModal"
          role="dialog"
          aria-modal="true"
        >
          <div class="iv-modal-inner" style="max-width: 720px; padding: 0">
            <div class="request-info-card" role="document" @click.stop>
              <header class="request-info-header">
                <h3>Запрос данных</h3>
              </header>

              <div class="request-info-body">
                <p>
                  Для получения данных по выбранному образцу, пожалуйста, напишите нам на почту
                  <a href="mailto:tomowebsolutions@yandex.ru" class="request-email">tomowebsolutions@yandex.ru</a>.
                </p>

                <p class="request-info-list-title">Данные, доступные для запроса:</p>

                <ul class="request-info-list">
                  <li>Исходные данные съёмки</li>
                  <li>Реконструированный стек</li>
                  <li>Лог-файл</li>
                  <li>Проект обработки данных</li>
                  <li>Дополнительные лабораторные данные</li>
                  <li>Физический образец</li>
                  <li>
                    Предобработанные данные:
                    <ul class="sublist">
                      <li>3D модель</li>
                      <li>Сегментированные (бинарные) стеки</li>
                      <li>Расчетные параметры по компонентам образца (объём, площадь поверхности, % содержания и т.д.)</li>
                    </ul>
                  </li>
                </ul>
              </div>

              <footer class="request-info-footer">
                <button class="action-btn" @click="closeRequestInfoModal">ОК</button>
              </footer>
            </div>
          </div>
        </div>
      </transition>
    </teleport>
  </main>
</template>

<script>
// ... Ваш <script> блок остается без изменений ...
export default {
  name: 'ItemView',
  data() {
    return {
      item: {},
      images: [],
      currentIndex: 0,
      noDataText: 'Данные отсутствуют',
      isModalOpen: false,
      modalIndex: 0,
      modalImageError: false,
      _fullSampleExts: ['bmp', 'png', 'jpg'],
      _foundCache: {},
      showRequestInfoModal: false,
    };
  },
  computed: {
    title() {
      return (
        (this.item.renamed_id && String(this.item.renamed_id).trim()) ||
        (this.item.internal_id && String(this.item.internal_id).trim()) ||
        (this.item.fadr && String(this.item.fadr).trim()) ||
        this.noDataText
      );
    },
    hasImage() {
      return Array.isArray(this.images) && this.images.length > 0 && !this.modalImageError;
    },
    currentImageSrc() {
      return this.images[this.currentIndex] || '';
    },
    currentModalImage() {
      return this.images[this.modalIndex] || this.images[0] || '';
    },
    combinedStructure() {
      const s1 = this.item.structure_i || (this.item.__raw && this.item.__raw.structure_i) || '';
      const s2 = this.item.structure_ii || (this.item.__raw && this.item.__raw.structure_ii) || '';
      if (s1 && s2) return `${s1}, ${s2}`;
      return s1 || s2 || this.noDataText;
    },
    currentIdCandidates() {
      const raw = this.item || this.item.__raw || {};
      return [
        String(this.item.internal_id || raw.internal_id || '').trim(),
        String(this.item.renamed_id || raw.renamed_id || '').trim(),
        String(this.item.fadr || raw.fadr || '').trim(),
        String(this.item.id || raw.id || '').trim(),
      ].filter(Boolean);
    },
    isFullSample() {
      const keys = Object.keys(FULL_SAMPLE_DOWNLOADS).map(String);
      if (!keys.length) return false;
      for (const cand of this.currentIdCandidates) {
        if (keys.includes(String(cand))) return true;
      }
      return false;
    },
    downloadUrl() {
      for (const cand of this.currentIdCandidates) {
        if (FULL_SAMPLE_DOWNLOADS[String(cand)]) return FULL_SAMPLE_DOWNLOADS[String(cand)];
      }
      return null;
    },
    showGeoLabSections() {
      try {
        const raw = this.item || this.item.__raw || {};
        const cat = (this.item.gis_category || raw.gis_category || '').toString().trim().toLowerCase();
        if (!cat) return false;
        const keywords = ['геолог', 'осад', 'палеонтолог'];
        return keywords.some(k => cat.includes(k));
      } catch (e) {
        return false;
      }
    },
  },
  methods: {
    async fetchItem() {
      const url = `/api/v1/item/${this.$route.params.id}`;
      try {
        const resp = await fetch(url);
        if (!resp.ok) throw new Error(`Не удалось загрузить образец (${resp.status})`);
        this.item = await resp.json();
      } catch (error) {
        console.error('Fetch item error:', error);
      }
    },
    async findImages() {
      this.images = [];
      this.modalImageError = false;
      const extract = arr =>
        Array.isArray(arr)
          ? arr.map(p => (typeof p === 'string' ? p : p && p.url ? p.url : null)).filter(Boolean)
          : [];
      const pi = extract(this.item.preview_images);
      if (pi.length) {
        this.images = pi;
        this.currentIndex = 0;
        return;
      }
      const rawPi = extract(this.item.__raw && this.item.__raw.preview_images);
      if (rawPi.length) {
        this.images = rawPi;
        this.currentIndex = 0;
        return;
      }
      if (this.item.__raw && this.item.__raw.image_url) {
        this.images = [this.item.__raw.image_url];
        this.currentIndex = 0;
        return;
      }
      if (this.item.image_url) {
        this.images = [this.item.image_url];
        this.currentIndex = 0;
        return;
      }
      const raw = this.item.__raw || this.item;
      const fadr = (this.item.fadr || raw.fadr || '').toString().trim();
      const idKey = (this.item.renamed_id || this.item.internal_id || raw.internal_id || this.item.id || raw.id || '')
        .toString()
        .trim();
      if (!fadr || !idKey) {
        this.images = [];
        return;
      }
      const cacheKey = `${fadr}||${idKey}`;
      if (this._foundCache[cacheKey]) {
        this.images = Array.isArray(this._foundCache[cacheKey]) ? [...this._foundCache[cacheKey]] : [];
        this.currentIndex = 0;
        return;
      }
      const found = [];
      const basePath = `/previews/${encodeURIComponent(fadr)}`;
      if (this.isFullSample) {
        for (let n = 1; n <= 4; n++) {
          const suffix = `_${n}`;
          let foundForSuffix = null;
          for (const ext of this._fullSampleExts) {
            const candidate = `${basePath}/${encodeURIComponent(idKey)}${suffix}.${ext}`;
            // eslint-disable-next-line no-await-in-loop
            const ok = await this._tryLoadImage(candidate);
            if (ok) {
              foundForSuffix = candidate;
              break;
            }
          }
          if (foundForSuffix) found.push(foundForSuffix);
          if (found.length >= 4) break;
        }
        if (!found.length) {
          for (const ext of this._fullSampleExts) {
            const candidate = `${basePath}/${encodeURIComponent(idKey)}.${ext}`;
            // eslint-disable-next-line no-await-in-loop
            if (await this._tryLoadImage(candidate)) {
              found.push(candidate);
              break;
            }
          }
        }
      } else {
        const candidate = `${basePath}/${encodeURIComponent(idKey)}_1.bmp`;
        const ok = await this._tryLoadImage(candidate);
        if (ok) found.push(candidate);
      }
      this._foundCache[cacheKey] = found.length ? [...found] : [];
      this.images = [...this._foundCache[cacheKey]];
      this.currentIndex = 0;
    },
    _tryLoadImage(url) {
      return new Promise(resolve => {
        try {
          const img = new Image();
          img.onload = () => resolve(true);
          img.onerror = () => resolve(false);
          img.src = url + (url.includes('?') ? '&_=' : '?_=') + Date.now();
        } catch (e) {
          resolve(false);
        }
      });
    },
    isEmpty(v) {
      return v === null || v === undefined || (typeof v === 'string' && v.trim() === '');
    },
    displayValue(field) {
      const primary = (this.item && (this.item[field.key] ?? (this.item.__raw && this.item.__raw[field.key]))) ?? undefined;
      const fallback =
        field.fallbackKey ? this.item && (this.item[field.fallbackKey] ?? (this.item.__raw && this.item.__raw[field.fallbackKey])) : undefined;
      const value = this.isEmpty(primary) ? fallback : primary;
      return this.isEmpty(value) ? this.noDataText : value;
    },
    formatNumberField(key, digits = 2) {
      const raw = (this.item && (this.item[key] ?? (this.item.__raw && this.item.__raw[key]))) ?? null;
      if (raw === null || raw === undefined || raw === '') return this.noDataText;
      const n = Number(String(raw).replace(',', '.'));
      if (!Number.isFinite(n)) return this.noDataText;
      return Number.isFinite(n) ? n.toFixed(digits) : this.noDataText;
    },
    openRequestInfoModal() {
      this.showRequestInfoModal = true;
    },
    closeRequestInfoModal() {
      this.showRequestInfoModal = false;
    },
    onRequestData() {
      console.info('Запросить данные по образцу → демоверсия (используйте окно с инструкцией)');
    },
    onImageError(idx) {
      return () => {
        try {
          if (this.images && this.images.length > idx) {
            this.images.splice(idx, 1);
            if (this.currentIndex >= this.images.length) this.currentIndex = Math.max(0, this.images.length - 1);
          }
        } catch (e) {}
      };
    },
    openModal(startIndex = 0) {
      if (!this.images || this.images.length === 0) return;
      this.modalIndex = Math.min(Math.max(0, startIndex), this.images.length - 1);
      this.isModalOpen = true;
      this.modalImageError = false;
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', this.onKeydown);
    },
    closeModal() {
      this.isModalOpen = false;
      this.modalImageError = false;
      document.body.style.overflow = '';
      window.removeEventListener('keydown', this.onKeydown);
    },
    nextImage() {
      if (!this.images || this.images.length <= 1) return;
      this.modalIndex = (this.modalIndex + 1) % this.images.length;
    },
    prevImage() {
      if (!this.images || this.images.length <= 1) return;
      this.modalIndex = (this.modalIndex - 1 + this.images.length) % this.images.length;
    },
    onKeydown(e) {
      if (!this.isModalOpen) return;
      if (e.key === 'Escape') this.closeModal();
      else if (e.key === 'ArrowRight') this.nextImage();
      else if (e.key === 'ArrowLeft') this.prevImage();
    },
    onModalImageError() {
      this.modalImageError = true;
    },
  },
  async mounted() {
    await this.fetchItem();
    await this.findImages();
  },
  watch: {
    '$route.params.id': {
      immediate: false,
      async handler() {
        await this.fetchItem();
        await this.findImages();
      },
    },
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeydown);
    document.body.style.overflow = '';
  },
};

const FULL_SAMPLE_DOWNLOADS = {
  ABB_001: 'https://disk.360.yandex.ru/d/nbQkDy_KUeRQQA',
  ADU_001: 'https://disk.360.yandex.ru/d/oRiNcq3Bx95giw',
  AFT_019: 'https://disk.360.yandex.ru/d/xIyi7HSaUDQ0BQ',
  AOH_001: 'https://disk.360.yandex.ru/d/zzjA5rpug34PcQ',
  AOV_003: 'https://disk.360.yandex.ru/d/KIZ1Y7i6FAgIeA',
  AYP_154: 'https://disk.360.yandex.ru/d/rFshG4f2OTZqqQ',
  BBQ_001: 'https://disk.360.yandex.ru/d/Y2MjzLiyMTVwJA',
  BEE_001: 'https://disk.360.yandex.ru/d/JvorTfJD4RrmEw',
};
</script>

<style scoped>
/* ========== 🎨 ПЕРЕМЕННЫЕ ДИЗАЙНА 🎨 ========== */
/* Эти переменные должны быть в global.css для консистентности */
:root {
  --bg-color: #0b111e;
  --card-bg: #121a2a;
  --text-primary: #e2e8f0;
  --text-muted: #94a3b8;
  --accent: #75e4f5;
  --accent-glow: rgba(117, 228, 245, 0.15);
  --border-color: rgba(117, 228, 245, 0.1);
  --dark-accent-bg: #1a2f3a;
}

/* ========== 🏗️ ОСНОВНЫЕ СТИЛИ 🏗️ ========== */
.item-page {
  background: var(--bg-color);
  min-height: calc(100vh - 120px);
  padding: 24px 16px;
  box-sizing: border-box;
}

.item-shell {
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(320px, 1fr) minmax(420px, 1.2fr);
  gap: 24px;
  align-items: start;
}

/* Общий стиль для левой и правой колонок */
.left-pane {
  background: var(--card-bg);
  border-radius: 16px;
  padding: 24px;
  border: 1px solid var(--border-color);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.right-pane {
  background: var(--card-bg);
  border-radius: 16px;
  border: 1px solid var(--border-color);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  height: 100%;
  position: relative;
}

.panel {
  position: absolute;
  inset: 0;
  padding: 24px;
  overflow-y: auto;
}

/* ========== 🖋️ ТИПОГРАФИКА И ТЕКСТ 🖋️ ========== */
.item-title {
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 4px 0;
  color: var(--text-primary);
}

.subsection-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 12px 0;
  padding-bottom: 12px;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color);
}

/* ========== 🖼️ ГАЛЕРЕЯ 🖼️ ========== */
.image-wrap {
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 12px;
  overflow: hidden;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  border: 1px solid var(--border-color);
}

.image-wrap--empty {
  background: var(--dark-accent-bg);
  border: 2px dashed var(--border-color);
}

.image-frame {
  width: 100%;
  height: 100%;
  cursor: zoom-in;
}

.image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.img-counter {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(11, 17, 30, 0.7);
  backdrop-filter: blur(5px);
  color: var(--text-primary);
  padding: 6px 12px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 13px;
  border: 1px solid var(--border-color);
}

.no-image-text-inline {
  font-weight: 600;
  color: var(--text-muted);
}

/* Пагинация */
.pager {
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
}

.pager-dot {
  border: none;
  background: var(--dark-accent-bg);
  border-radius: 50%;
  width: 10px;
  height: 10px;
  padding: 0;
  cursor: pointer;
  transition: all 0.2s ease;
}
.pager-dot:hover {
  background: var(--accent);
}
.pager-dot--active {
  background: var(--accent);
  transform: scale(1.2);
  box-shadow: 0 0 10px var(--accent-glow);
}

/* ========== 🚀 КНОПКИ ДЕЙСТВИЙ 🚀 ========== */
.actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid var(--border-color);
}

.action-btn {
  flex-grow: 1;
  text-align: center;
  border-radius: 8px;
  background: var(--accent);
  color: var(--bg-color);
  border: none;
  padding: 12px 16px;
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 0 20px var(--accent-glow);
}
.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 25px rgba(117, 228, 245, 0.4);
}

.action-btn:not(.download-btn) {
  background: var(--dark-accent-bg);
  color: var(--accent);
  border: 1px solid var(--accent);
  box-shadow: none;
}
.action-btn:not(.download-btn):hover {
  background: var(--accent);
  color: var(--bg-color);
  box-shadow: 0 4px 25px rgba(117, 228, 245, 0.4);
}

/* ========== 📊 ПАНЕЛЬ С ДАННЫМИ 📊 ========== */
.subsection {
  margin-top: 24px;
}
.subsection:first-child {
  margin-top: 0;
}

.meta {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.row {
  display: grid;
  grid-template-columns: minmax(180px, 1fr) 2fr;
  align-items: start;
  gap: 16px;
  padding: 8px 0;
  border-bottom: 1px solid var(--border-color);
}
.row:last-child {
  border-bottom: none;
}

dt {
  color: var(--text-muted);
  font-size: 15px;
  font-weight: 400;
}
dd {
  margin: 0;
  font-size: 15px;
  color: var(--text-primary);
  font-weight: 500;
  word-break: break-word;
}

/* ========== 🖼️ МОДАЛЬНОЕ ОКНО (СТИЛИ ИЗ LIFEVIEW) 🖼️ ========== */
/* ✨ ИЗМЕНЕНО: Стили для модального окна просмотра изображений (взяты из LifeView) */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.iv-modal {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2147483000;
  background: rgba(11, 17, 30, 0.8);
  backdrop-filter: blur(8px);
  padding: 60px; /* Большой паддинг для создания безопасной зоны */
  box-sizing: border-box;
}

.iv-modal-inner {
  position: relative;
  max-width: 100%;
  max-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.iv-modal-img {
  max-width: 100%;
  max-height: 100%;
  border-radius: 12px;
  object-fit: contain; /* Ключевое свойство для вписывания изображения */
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.6);
}

.iv-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(30, 41, 59, 0.8);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  width: 48px;
  height: 48px;
  border-radius: 50%;
  font-size: 24px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s ease;
}
.iv-nav:hover {
  background-color: var(--accent);
  color: var(--bg-color);
}
.iv-nav.left {
  left: 16px;
}
.iv-nav.right {
  right: 16px;
}

.iv-modal-close {
  position: fixed;
  top: 18px;
  right: 18px;
  z-index: 20;
  width: 44px;
  height: 44px;
  background: rgba(30, 41, 59, 0.8);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  border-radius: 50%;
  cursor: pointer;
  font-size: 20px;
  transition: all 0.2s ease;
}
.iv-modal-close:hover {
  background-color: var(--accent);
  color: var(--bg-color);
  transform: rotate(90deg);
}

.iv-modal-indicator {
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(11, 17, 30, 0.7);
  backdrop-filter: blur(5px);
  color: var(--text-primary);
  padding: 8px 16px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 14px;
  border: 1px solid var(--border-color);
}

.noimage-box {
  text-align: center;
  color: var(--text-primary);
  padding: 32px;
  border-radius: 12px;
  background: var(--card-bg);
  border: 1px solid var(--border-color);
}
.noimage-ico {
  font-size: 48px;
}
.noimage-text {
  font-size: 20px;
  font-weight: 700;
}
.noimage-sub {
  color: var(--text-muted);
}

/* ========== ℹ️ МОДАЛЬНОЕ ОКНО ЗАПРОСА (без изменений) ℹ️ ========== */
.request-info-card {
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  width: 100%;
  max-height: calc(100vh - 120px);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.35);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.request-info-header {
  padding: 16px 24px;
  border-bottom: 1px solid var(--border-color);
  background: var(--dark-accent-bg);
}
.request-info-header h3 {
  margin: 0;
  font-size: 18px;
  color: var(--text-primary);
}

.request-info-body {
  padding: 24px;
  color: var(--text-primary);
  line-height: 1.7;
  font-size: 16px;
  overflow: auto;
}

.request-info-body p {
  margin: 0 0 16px 0;
}
.request-info-list-title {
  font-weight: 700;
  margin-bottom: 12px;
  color: var(--text-primary);
}

.request-info-list {
  margin: 0;
  padding-left: 20px;
}
.request-info-list li {
  margin-bottom: 10px;
  color: var(--text-muted);
}

.request-info-list .sublist {
  margin-top: 10px;
  padding-left: 20px;
}
.request-email {
  font-weight: 700;
  color: var(--accent);
  text-decoration: none;
}
.request-email:hover {
  text-decoration: underline;
}

.request-info-footer {
  padding: 16px 24px;
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: flex-end;
  background: var(--dark-accent-bg);
}

/* ========== 📱 АДАПТИВНОСТЬ 📱 ========== */
@media (max-width: 980px) {
  .item-shell {
    grid-template-columns: 1fr;
    align-items: stretch;
  }
  .right-pane {
    height: auto;
    position: static;
  }
  .panel {
    position: static;
    padding: 24px;
    overflow-y: visible;
  }
}

@media (max-width: 576px) {
  .item-page {
    padding: 16px 8px;
  }
  .left-pane,
  .right-pane,
  .panel {
    padding: 16px;
  }
  .row {
    grid-template-columns: 1fr;
    gap: 8px;
    padding: 12px 0;
  }
  dt {
    font-weight: 600;
    color: var(--text-primary);
  }

  /* ✨ ИЗМЕНЕНО: Адаптация модального окна для мобильных */
  .iv-modal {
    padding: 20px;
  }
  .iv-modal-inner {
    max-width: 100vw;
    max-height: 100vh;
  }
  .iv-nav {
    display: none;
  }
  .iv-modal-close {
    top: 10px;
    right: 10px;
  }
  .iv-modal-indicator {
    bottom: 10px;
  }
}

</style>